import visit from 'unist-util-visit-parents';
import findAfter from 'unist-util-find-after';
import { getHeadingId, getHeadingText } from '../utils';
import { extractIdFromHtmlElement } from '../../../../../utils/htmlStriper.util';

/* inspired by remark-sectionize - https://github.com/rexxars/react-markdown/issues/309 */

const getIsHtmlParsedHeading = (node) =>
  node.type === 'parsedHtml' &&
  (node.value.trim().startsWith('<h3') || node.value.trim().startsWith('<h2'));

const getIsHtmlParsedHeadingInDepth = (node, depth) =>
  node.type === 'parsedHtml' && node.value.trim().startsWith(`<h${depth}`);

const getHeadingDepth = (node) => {
  if (node.type === 'heading') {
    return node.depth;
  }
  if (node.type === 'parsedHtml') {
    // eslint-disable-next-line radix
    return parseInt(node.value.trim()[2]);
  }
  return 0;
};

const shouldVisitNode = (visitDepth) => (node) => {
  const isHeadingInCurrentDepth =
    node.type === 'heading' && node.depth === visitDepth;
  const isHtmlParsedHeadingInCurrentDepth = getIsHtmlParsedHeadingInDepth(
    node,
    visitDepth,
  );
  return isHeadingInCurrentDepth || isHtmlParsedHeadingInCurrentDepth;
};

const shouldCreateNewSectionForNode = (node) => {
  const isHeading =
    node.type === 'heading' && (node.depth === 3 || node.depth === 2);
  const isHtmlParsedHeading = getIsHtmlParsedHeading(node);
  return isHeading || isHtmlParsedHeading;
};

const isSectionGeneratedFromHeading = (node) =>
  node.type === 'section' && node.data && node.data.generatedFromHeading;

const isEndOfSection = (node) =>
  shouldCreateNewSectionForNode(node) || isSectionGeneratedFromHeading(node);

export function sectionize(markdownPath) {
  function transformer(tree) {
    const MAX_HEADING_DEPTH = 3;

    for (let depth = 2; depth <= MAX_HEADING_DEPTH; depth++) {
      visit(tree, shouldVisitNode(depth), sectionizeTree);
    }

    function sectionizeTree(node, ancestors) {
      const start = node;
      const parent = ancestors[ancestors.length - 1];

      const end = findAfter(parent, start, isEndOfSection);

      const startIndex = parent.children.indexOf(start);
      const endIndex = parent.children.indexOf(end);

      const between = parent.children.slice(
        startIndex,
        endIndex > 0 ? endIndex : undefined,
      );

      const nodeDepth = getHeadingDepth(node);
      const idFromHtmlElement = extractIdFromHtmlElement(node);
      const sectionId = getHeadingId(
        { id: idFromHtmlElement, ...node },
        markdownPath,
      );

      const section = {
        type: 'section',
        children: between,
        position: parent.position,
        id: sectionId,
        data: {
          markdownPath,
          headingText: getHeadingText(node),
          headingLevel: nodeDepth,
          generatedFromHeading: true,
        },
      };

      parent.children.splice(startIndex, section.children.length, section);
    }
    return tree;
  }

  return transformer;
}
